<template>
    <div>
        <div v-if="!busy">
            <h5 v-if="!is_mine">{{ $t('please-complete-and-submit-to-continue') }}</h5>
            <!-- question carousel -->
            <div class="card p-3 pb-2 question bg-secondary">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <h5 class="fw-bold mb-0">Question {{ currentQuestionIndex + 1 }} of {{ quiz.questions.length }}</h5>
                    <div v-if="quiz.questions.length > 1" class="progress" style="width: 60%; height: 10px;">
                        <div class="progress-bar" role="progressbar" 
                            :style="{ width: `${(currentQuestionIndex + 1) / quiz.questions.length * 100}%` }" 
                            :aria-valuenow="currentQuestionIndex + 1" 
                            aria-valuemin="0" 
                            :aria-valuemax="quiz.questions.length">
                        </div>
                    </div>
                </div>
                <div v-if="quiz.questions.length > 0">
                    <h5 class="mb-0">{{ currentQuestion.question }}</h5>
                    <hr />
                    <span v-if="currentQuestion.image" class="fw-bold text-primary mt-1 me-2 pointer" style="float: right;" @click="viewImage(currentQuestion)">
                        {{ $t('view-image') }}
                    </span>
                    <div v-for="answer in currentQuestion.answers" :key="answer.id" class="form-check answer-container" 
                        :class="{
                            'selected-answer': isResultsMode && userAnswerForCurrentQuestion === answer.id,
                            'correct-answer': isResultsMode && answer.id === getCorrectAnswerId(currentQuestion.id),
                            'incorrect-answer': isResultsMode && userAnswerForCurrentQuestion === answer.id && !isAnswerCorrect(answer.id, currentQuestion.id)
                        }"
                        :style="{ 'padding-left': isResultsMode ? '10px' : '25px' }">
                        <input
                            v-if="!isResultsMode"
                            :id="`answer-${answer.id}`"
                            v-model="selectedAnswers[currentQuestion.id]"
                            type="radio"
                            :name="`question-${currentQuestion.id}`"
                            :value="answer.id"
                            class="form-check-input"
                        />
                        <h6>
                            {{ answer.answer }}
                            <!-- Show correct/incorrect indicators in results mode -->
                            <template v-if="isResultsMode && userAnswerForCurrentQuestion === answer.id">
                                <i v-if="isAnswerCorrect(answer.id, currentQuestion.id)" 
                                class="fas fa-check-circle text-success ms-2"></i>
                                <i v-else class="fas fa-times-circle text-danger ms-2"></i>
                            </template>
                            <i v-if="isResultsMode && answer.id === getCorrectAnswerId(currentQuestion.id) && userAnswerForCurrentQuestion !== answer.id" 
                            class="fas fa-check text-success ms-2"></i>
                        </h6>
                    </div>
                </div>
            </div>
            <div v-if="isResultsMode" class="alert alert-info mt-3">
                <h5 class="mb-0">{{ $t('quiz-results') }}</h5>
                <p class="mb-1">You scored {{ results.score.percentage }}% ({{ results.score.correct }} out of {{ results.score.total }})</p>
                <p class="mb-0 small">{{ $t('submitted-on') }} {{ formatDate(results.submitted_at) }}</p>
            </div>
            <!-- navigation buttons -->
            <div class="d-flex justify-content-between mt-3 mb-3">
                <button class="btn btn-outline-secondary" 
                    :disabled="currentQuestionIndex === 0"
                    @click="prevQuestion">
                    <i class="fas fa-chevron-left me-2"></i>
                    {{ $t('previous-question') }}
                </button>
                <div class="question-indicators d-flex">
                    <span v-for="(question, index) in quiz.questions" 
                        :key="'indicator-' + question.id" 
                        class="question-indicator mx-1"
                        :class="{
                            'active': currentQuestionIndex === index,
                            'answered': selectedAnswers[question.id] !== undefined
                        }"
                        @click="goToQuestion(index)">
                    </span>
                </div>
                <button class="btn btn-outline-primary" 
                    :disabled="currentQuestionIndex === quiz.questions.length - 1"
                    @click="nextQuestion" >
                    {{ $t('next-question') }}
                    <i class="fas fa-chevron-right ms-2"></i>
                </button>
            </div>
            <!-- Submit  -->
            <div v-if="!isResultsMode && !is_mine" class="mt-4 text-center">
                <button class="btn btn-primary px-4" @click="submit">{{ $t('submit') }}</button>
                <div v-if="!allQuestionsAnswered" class="mt-2 text-muted small">
                    <i class="fas fa-exclamation-circle me-1"></i>
                    {{ $t('please-answer-all-questions-before-submitting') }}
                </div>
            </div>
        </div>
        <div v-if="busy" class="text-center" style="padding-top: 11rem;">
            <i class="fas fa-5x fa-spinner"></i>
        </div>
        <b-modal v-model="showQuestionImagePreviewModal" hide-footer hide-header size="lg">
            <span class="fs-4 pointer pe-0" style="float: right;" @click="showQuestionImagePreviewModal = false">
                <i class="fa-solid fa-xmark"></i>
            </span>
            <div>
                <img v-if="selectedQuestion && selectedQuestion.image" 
                    :src="selectedQuestion.image_url"
                    alt="Question image"
                    class="img-fluid rounded"
                    style="max-height: 70vh;" />
            </div>
        </b-modal>
    </div>
</template>
<script>
import moment from 'moment';

export default {
    props: {
        quiz: {
            type: Object,
            default: null,
        },
        type: {
            type: String,
            default: null,
        },
        course_id: {
            type: Number,
            default: null,
        },
        module_id: {
            type: Number,
            default: null,
        },
        results: {
            type: Object,
            default: null,
        },
        is_mine : {
            type: Boolean,
            default: false,
        }
    },
    data() {
        return {
            busy: false,
            selectedAnswers: {},
            currentQuestionIndex: 0,
            selectedQuestion: null,
            showQuestionImagePreviewModal: false,
        };
    },
    computed: {
        currentQuestion() {
            return this.quiz.questions[this.currentQuestionIndex] || {};
        },
        allQuestionsAnswered() {
            return this.quiz.questions.every(
                (question) => this.selectedAnswers[question.id] !== undefined
            );
        },
        isResultsMode() {
            return this.results && this.results.answers && this.results.answers.length > 0;
        },
        userAnswerForCurrentQuestion() {
            if (!this.isResultsMode || !this.currentQuestion) return null;
            const questionResult = this.results.answers.find(a => a.question_id === this.currentQuestion.id);
            return questionResult ? questionResult.answer_id : null;
        },
    },
    watch: {
        quiz: {
            handler() {
                // reset component state when quiz changes
                this.selectedAnswers = {};
                this.currentQuestionIndex = 0;
            },
            deep: true
        },
        module_id() {
            this.selectedAnswers = {};
            this.currentQuestionIndex = 0;
        }
    },
    mounted() {
        this.initializeFromResults();
    },
    methods: {
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return moment.utc(dateString).local().format("MMMM D, YYYY • h:mm a");
        },
        viewImage(question) {
            this.selectedQuestion = question;
            this.showQuestionImagePreviewModal = true;
        },
        prevQuestion() {
            if (this.currentQuestionIndex > 0) {
                this.currentQuestionIndex--;
            }
        },
        nextQuestion() {
            if (this.currentQuestionIndex < this.quiz.questions.length - 1) {
                this.currentQuestionIndex++;
            }
        },
        goToQuestion(index) {
            if (index >= 0 && index < this.quiz.questions.length) {
                this.currentQuestionIndex = index;
            }
        },
        submit() {
            // check if all questions have an answer selected
            const allAnswered = this.allQuestionsAnswered;

            if (!allAnswered) {
                this.$swal.fire({
                    icon: 'warning',
                    title: 'Incomplete!',
                    text: 'Please answer all questions before submitting.',
                });
                return;
            }

            this.busy = true;

            this.$http.post('quiz/attempt', {
                course_id: this.course_id,
                quiz_id: this.quiz.id,
                module_id: this.module_id, // add module_id if it exists
                answers: this.selectedAnswers,
                completed: allAnswered,
                pass: true,
            }).then(response => {
                // if response contains quiz results, set them immediately
                if (response.data && response.data.results) {
                    this.results = response.data.results;
                }

                this.busy = false;

                let message = 'All your answers have been submitted.';
                this.$swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: message,
                }).then(() => {
                    this.$emit('quiz-completed', this.type);
                });
            }).catch(error => {
                this.busy = false;
                console.error('Error submitting quiz:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'There was a problem submitting your quiz. Please try again.',
                });
            });
        },
        getCorrectAnswerId(questionId) {
            const question = this.quiz.questions.find(q => q.id === questionId);
            if (!question) return null;
            // find the correct answer
            const correctAnswer = question.answers.find(a => a.is_correct);
            return correctAnswer ? correctAnswer.id : null;
        },
        isAnswerCorrect(answerId, questionId) {
            if (!this.isResultsMode) return false;
            const result = this.results.answers.find(a => a.question_id === questionId);
            return result && result.is_correct && result.answer_id === answerId;
        },
        initializeFromResults() {
            if (this.isResultsMode) {
                // pre-select the user's previous answers
                this.results.answers.forEach(answer => {
                    this.$set(this.selectedAnswers, answer.question_id, answer.answer_id);
                });
            }
        },
    },
};
</script>
<style scoped>
.question {
    margin-bottom: 1rem;
    min-height: 300px;
}
.question-indicator {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: #dee2e6;
    cursor: pointer;
}
.question-indicator.active {
    background-color: #005f9a;
    transform: scale(1.2);
}
.question-indicator.answered {
    background-color: #28a745;
}
.progress {
    border-radius: 5px;
}
.progress-bar {
    background-color: #007bff;
    transition: width 0.3s ease;
}
.answer-container {
    padding: 10px;
    border-radius: 4px;
    margin-bottom: 8px;
}
.selected-answer {
    background-color: rgba(0, 123, 255, 0.1);
}
.correct-answer {
    background-color: rgba(40, 167, 69, 0.1);
    border-left: 3px solid #28a745;
}
.incorrect-answer {
    background-color: rgba(220, 53, 69, 0.1);
    border-left: 3px solid #dc3545;
}
h6 {
    margin-bottom: 0;
}
.progress-bar {
    background-color: #005f9a;
}
</style>